/**
 * Firestore Service
 * 
 * Handles all Firestore operations for user interests and profile data.
 * Provides a clean API for Firestore CRUD operations.
 */

import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { firestore } from './firebase';

/**
 * Save or update user interests in Firestore with retry logic and offline support
 * @param {string} userId - Firebase user UID
 * @param {Array<string>} interests - Array of selected interests (e.g., ['poems', 'novels', 'stories'])
 * @param {number} retries - Number of retry attempts
 * @returns {Promise<void>}
 */
export const saveUserInterests = async (userId, interests, retries = 3) => {
  // Save to localStorage immediately for offline support
  const localStorageKey = `user_interests_${userId}`;
  localStorage.setItem(localStorageKey, JSON.stringify(interests));
  
  // Also save pending writes for retry
  const pendingKey = `pending_interests_${userId}`;
  localStorage.setItem(pendingKey, JSON.stringify({ interests, timestamp: Date.now() }));

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const userRef = doc(firestore, 'users', userId);
      await setDoc(
        userRef,
        {
          interests: interests,
          interestsUpdatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      
      // Success - remove pending write
      localStorage.removeItem(pendingKey);
      return;
    } catch (error) {
      const isOfflineError = error.code === 'unavailable' || error.message?.includes('offline');
      
      if (isOfflineError && attempt < retries) {
        // Wait before retry with exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        continue;
      }
      
      // If offline, the data is saved in localStorage and will sync when online
      if (isOfflineError) {
        console.warn('User interests saved locally. Will sync when online.');
        // Don't throw error - data is saved locally
        return;
      }
      
      // If it's the last attempt or not an offline error, log but don't throw
      if (attempt === retries || !isOfflineError) {
        console.error('Error saving user interests:', error);
        // Don't throw - data is already in localStorage
        // The app will try to sync when online
        return;
      }
    }
  }
};

/**
 * Get user interests from Firestore with retry logic and offline support
 * @param {string} userId - Firebase user UID
 * @returns {Promise<Array<string>|null>} Array of interests or null if not found
 */
export const getUserInterests = async (userId, retries = 2) => {
  // Try to get from localStorage first as fallback
  const localStorageKey = `user_interests_${userId}`;
  const cachedInterests = localStorage.getItem(localStorageKey);
  
  if (cachedInterests) {
    try {
      const parsed = JSON.parse(cachedInterests);
      if (Array.isArray(parsed)) {
        // Return cached data immediately, then try to sync
        setTimeout(() => {
          getUserInterests(userId, 0).catch(() => {
            // Silently fail sync attempt
          });
        }, 100);
        return parsed;
      }
    } catch (e) {
      // Invalid cache, continue to Firestore
    }
  }

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const userRef = doc(firestore, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        const interests = data.interests || null;
        
        // Cache in localStorage
        if (interests !== null) {
          localStorage.setItem(localStorageKey, JSON.stringify(interests));
        }
        
        return interests;
      }
      
      // Document doesn't exist, return null
      return null;
    } catch (error) {
      const isOfflineError = error.code === 'unavailable' || error.message?.includes('offline');
      
      if (isOfflineError && attempt < retries) {
        // Wait before retry with exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        continue;
      }
      
      // If offline and we have cached data, return it
      if (isOfflineError && cachedInterests) {
        try {
          return JSON.parse(cachedInterests);
        } catch (e) {
          // Invalid cache
        }
      }
      
      // If it's the last attempt or not an offline error, log and return null
      if (attempt === retries || !isOfflineError) {
        console.warn('Error getting user interests:', error.message || error);
        // Return null instead of throwing to prevent blocking UI
        return null;
      }
    }
  }
  
  return null;
};

/**
 * Check if user has completed onboarding (has interests or has skipped)
 * @param {string} userId - Firebase user UID
 * @returns {Promise<boolean>} True if user has interests or has skipped (empty array), false otherwise
 */
export const hasCompletedOnboarding = async (userId) => {
  try {
    // Check localStorage first for faster response
    const localStorageKey = `user_interests_${userId}`;
    const cachedInterests = localStorage.getItem(localStorageKey);
    
    if (cachedInterests) {
      try {
        const parsed = JSON.parse(cachedInterests);
        if (Array.isArray(parsed)) {
          return true; // User has completed onboarding (even if empty array)
        }
      } catch (e) {
        // Invalid cache, continue to Firestore
      }
    }
    
    const interests = await getUserInterests(userId, 1); // Only 1 retry for onboarding check
    // Return true if interests exist (even if empty array means they skipped)
    return interests !== null && Array.isArray(interests);
  } catch (error) {
    console.warn('Error checking onboarding status:', error.message || error);
    // On error, check localStorage as fallback
    const localStorageKey = `user_interests_${userId}`;
    const cachedInterests = localStorage.getItem(localStorageKey);
    if (cachedInterests) {
      try {
        const parsed = JSON.parse(cachedInterests);
        return Array.isArray(parsed);
      } catch (e) {
        // Invalid cache
      }
    }
    return false;
  }
};

/**
 * Save user profile data to Firestore
 * @param {string} userId - Firebase user UID
 * @param {Object} userData - User profile data
 * @returns {Promise<void>}
 */
export const saveUserProfileToFirestore = async (userId, userData) => {
  try {
    const userRef = doc(firestore, 'users', userId);
    await setDoc(
      userRef,
      {
        ...userData,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error saving user profile to Firestore:', error);
    throw error;
  }
};

/**
 * Get user profile data from Firestore
 * @param {string} userId - Firebase user UID
 * @returns {Promise<Object|null>} User profile data or null if not found
 */
export const getUserProfileFromFirestore = async (userId) => {
  try {
    const userRef = doc(firestore, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return userSnap.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting user profile from Firestore:', error);
    throw error;
  }
};

/**
 * Update user profile in Firestore
 * @param {string} userId - Firebase user UID
 * @param {Object} updates - Partial user data to update
 * @returns {Promise<void>}
 */
export const updateUserProfileInFirestore = async (userId, updates) => {
  try {
    const userRef = doc(firestore, 'users', userId);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating user profile in Firestore:', error);
    throw error;
  }
};

